import { FC, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { overviewLoadingSelector } from "@/store/commitments/selectors/overview/loading/overviewLoadingSelector";
import { overviewRealCostSelector } from "@/store/commitments/selectors/overview/overviewRealCostSelector";
import { money } from "@/utils/numeral/money";
import { CommitmentsPageHeaderMetadataCard } from "@/components/pages/commtiments/common/components/metadata-card/CommitmentsPageHeaderMetadataCard";
import { organisationQuarterStartMonthSelector } from "@/store/common/selectors/org-current-settings/organisationQuarterStartMonthSelector";
import { commitmentsDatesSelector } from "@/store/commitments/selectors/common/view-options/commitmentsDatesSelector";
import { generateFromToFromDates } from "@/components/pages/commtiments/utilization-and-coverage/utils/helpers/generateFromToFromDates";
import { overviewCoveredCostBreakdownSelector } from "@/store/commitments/selectors/overview/overviewCoveredCostBreakdownSelector";
import { CommitmentType } from "@/services/cloudchipr.api";
import {
  setCommitmentsDataViewTab,
  setCommitmentsTypeTab,
} from "@/store/commitments/commitmentsSlice";

export const CommitmentsOverviewCoverageMetadataCoverageCard: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const realCost = useAppSelector(overviewRealCostSelector);
  const quarterStartMonth = useAppSelector(
    organisationQuarterStartMonthSelector,
  );
  const dates = useAppSelector(commitmentsDatesSelector);
  const datesFromTo = useMemo(() => {
    return generateFromToFromDates(dates, quarterStartMonth);
  }, [dates, quarterStartMonth]);
  const loading = useAppSelector(overviewLoadingSelector);

  const overviewCoveredCostBreakdown = useAppSelector(
    overviewCoveredCostBreakdownSelector,
  );

  const handleChipItemClick = useCallback(
    (commitmentType: CommitmentType) => {
      dispatch(setCommitmentsTypeTab(commitmentType));
      dispatch(setCommitmentsDataViewTab("coverage"));
      navigate({ search: `active=${commitmentType}` });
    },
    [dispatch, navigate],
  );

  if (!overviewCoveredCostBreakdown) {
    return null;
  }

  const {
    total: totalCoverage,
    reservation: reservationsTotalCoverage,
    savings_plan: savingsPlansTotalCoverage,
  } = overviewCoveredCostBreakdown;

  return (
    <CommitmentsPageHeaderMetadataCard
      primary="Coverage"
      loading={loading}
      value={realCost === null ? "N/A" : money(totalCoverage)}
      tooltip={`Total amount paid for commitments ${datesFromTo}`}
      chipData={{
        reservedInstances: {
          value: money(reservationsTotalCoverage),
          tooltipTitle: "View Reservations Coverage",
        },
        savingsPlans: {
          value: money(savingsPlansTotalCoverage),
          tooltipTitle: "View Savings Plans Coverage",
        },
        onClick: handleChipItemClick,
      }}
    />
  );
};
